// src/components/StreaksPreview.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const PreviewContainer = styled.div`
  padding: 40px 20px;
  background-color: var(--secondaryBackgroundColor);
  border-radius: 14px;
  color: var(--fontPrimaryColor);
  height: 100%;
  min-height: 300px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Header = styled.h2`
  text-align: center;
  font-weight: 500;
  margin: 0;
  font-size: 18px;
  font-family: "Montserrat", sans-serif;
  color: var(--fontPrimaryColor);
`;

const StreaksList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  margin: 0;
  max-height: 250px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #717d9b;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--secondaryBackgroundColor);
  }
`;

const StreakItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  font-size: 14px;
  list-style: none;
  opacity: 0.7;
  pointer-events: none;
`;

const StreakName = styled.span`
  font-weight: 500;
  color: var(--fontPrimaryColor);
`;

const StreakCount = styled.span`
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
  color: white;
  font-size: 12px;
  min-width: 50px;
  text-align: center;
`;

const BlurOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: ${(props) => (props.$active ? "blur(8px)" : "none")};
  background: ${(props) =>
    props.$active ? "rgba(0, 0, 0, 0.7)" : "transparent"};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${(props) => (props.$active ? 10 : -1)};
  opacity: ${(props) => (props.$active ? 1 : 0)};
  transition: all 0.3s ease;
`;

const UpgradeMessage = styled.div`
  background: var(--primaryColor);
  color: white;
  padding: 20px 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 80%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 11;

  h3 {
    margin: 0 0 10px 0;
    font-size: 1.2rem;
  }

  p {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    opacity: 0.9;
  }

  button {
    background: white;
    color: var(--primaryColor);
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
  }
`;

const InfoText = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 14px;
  text-align: center;
  margin: 10px 0;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  font-style: italic;
`;

const StreaksPreview = () => {
  const { t } = useTranslation();
  const [showUpgradeMessage, setShowUpgradeMessage] = useState(false);
  const [hovering, setHovering] = useState(false);

  // Pre-set demo streaks for FREE users
  const demoStreaks = [
    { id: 2, name: "Gym", streak: 14, color: "#3b82f6" },
    { id: 3, name: "Read 20 Pages", streak: 7, color: "#f59e0b" },
    { id: 1, name: "Morning Meditation", streak: 21, color: "#10b981" },
    { id: 4, name: "Drink 2l of Water", streak: 3, color: "#ef4444" },
    { id: 5, name: "2h+ Study", streak: 45, color: "#10b981" },
    {
      id: 6,
      name: "No Social Media Before Noon",
      streak: 12,
      color: "#8b5cf6",
    },
  ];

  const handleContainerClick = () => {
    setShowUpgradeMessage(true);
  };

  const handleUpgradeClick = () => {
    window.location.href = "/upgrade";
  };

  const handleCloseUpgrade = (e) => {
    e.stopPropagation();
    setShowUpgradeMessage(false);
    setHovering(false);
  };

  const handleMouseEnter = () => {
    setHovering(true);
  };

  const handleMouseLeave = () => {
    if (!showUpgradeMessage) {
      setHovering(false);
    }
  };

  return (
    <PreviewContainer
      onClick={handleContainerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Header>{t("streaks.title")}🔒</Header>
      {/* 
      <InfoText>
        Track your habit streaks to build consistency and motivation.
      </InfoText> */}

      <StreaksList>
        {demoStreaks.map((streak) => (
          <StreakItem key={streak.id}>
            <StreakName>{streak.name}</StreakName>
            <StreakCount
              style={{
                background: streak.color,
              }}
            >
              {streak.streak} day{streak.streak !== 1 ? "s" : ""}
            </StreakCount>
          </StreakItem>
        ))}
      </StreaksList>

      <InfoText>🔥 Longest streak: 45 days on "Evening Journal"</InfoText>

      <BlurOverlay $active={showUpgradeMessage || hovering}>
        {showUpgradeMessage ? (
          <UpgradeMessage onClick={(e) => e.stopPropagation()}>
            <h3>🔒 Streak Tracking Pro</h3>
            <p>
              Track unlimited habit streaks with smart analytics, milestone
              celebrations, and streak recovery features. Stay motivated with
              visual progress tracking.
            </p>
            <button onClick={handleUpgradeClick}>Upgrade to PRO</button>
            <button
              onClick={handleCloseUpgrade}
              style={{
                background: "transparent",
                color: "white",
                marginTop: "10px",
                fontSize: "0.85rem",
              }}
            >
              Maybe later
            </button>
          </UpgradeMessage>
        ) : hovering ? (
          <UpgradeMessage onClick={(e) => e.stopPropagation()}>
            <h3>Click to unlock Streak Tracking</h3>
            <p>
              This is a preview of Streak Tracking. Upgrade to PRO to track your
              actual habits and build real streaks with automatic tracking.
            </p>
          </UpgradeMessage>
        ) : null}
      </BlurOverlay>
    </PreviewContainer>
  );
};

export default StreaksPreview;
